import { Component, ErrorInfo, ReactNode } from "react";
import { ErrorDetails } from "./ErrorDetails";

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

/**
 * Error boundary component that catches React errors and displays a fallback UI.
 *  No display name required
 */
class ErrorBoundary extends Component<Props, State> {
  static displayName = "ErrorBoundary";
  public state: State = {
    hasError: false,
    errorMessage: "",
  };

  /**
   * Derives error state from caught errors.
   *
   * @param error - The caught error object
   */
  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, errorMessage: error.message };
  }

  /**
   * Logs error details to console when errors are caught.
   *
   * @param error - The caught error object
   * @param errorInfo - Additional error information from React
   */
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  /**
   * Renders children or error UI based on error state.
   */
  public render() {
    if (this.state.hasError) {
      return <ErrorDetails errorMessage={this.state.errorMessage} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
